import React, { useEffect, useState } from "react"
import {
    ActivityIndicator,
    BackHandler,
    Keyboard,
    KeyboardAvoidingView,
    Modal,
    Platform,
    Pressable,
    ScrollView,
    TouchableOpacity,
    View,
    useWindowDimensions
} from "react-native"
import { Icon } from "@rneui/base"
import { appColors, appFonts, fontSize } from "../../../themes/appTheme"
import Toast from "react-native-toast-message"
import ColorPicker from "react-native-wheel-color-picker"
import * as Yup from 'yup'
import Popover from "react-native-popover-view/dist/Popover"
import { useFormik, FormikErrors, FormikTouched } from "formik"
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from "@react-navigation/native"
import { config } from "../../../shared/environment"
import CommonDialogBox from "../../../shared/commonComponents/commonDialogBox"
import CommonHeader from "../../../shared/commonComponents/commonHeader"
import { OptionUpdatedValueType, OptionUpdatedJsonType, DialogType, NavigationType2, optionValue, RouteType } from "./optionModule"
import { useAppDispatch } from "../../../redux/hooks"
import { getOnePosOptionList, getOnePostDuplicateCheck, getOnePostOptionData, getOneUpdateOptionData } from "../../../redux/slices/optionSlice"
import { ThemeConsumer } from "@rneui/themed"
import { ZenButton, ZenCheckBox, ZenInput, ZenLoader } from "zen-core-elements"
import ZenText from "zen-core-elements/src/elements/zenText/zenText"
import { CustomModal } from "../../../shared/commonComponents/customModal"
import { SafeAreaView } from "react-native-safe-area-context"

let removeId: number[] = []

const COLOR_PRESETS = [
    '#000000', '#FFFFFF', '#64748B', '#EF4444', '#F97316',
    '#F59E0B', '#10B981', '#06B6D4', '#3B82F6', '#8B5CF6'
]

const AddEditOption = () => {
    const dispatch = useAppDispatch()
    const dimensions = useWindowDimensions()
    const isTablet = dimensions.width > 700

    const props: RouteProp<RouteType, "AddEditOption"> = useRoute()
    const navigation = useNavigation<NavigationProp<NavigationType2>>()

    const optionId = props?.params?.selectedId
    const edit = props?.params?.isEdit
    const isFocus = useIsFocused()

    const [isTransparentLoad, setIsTransparentLoad] = useState(false)
    const [optionData, setOptionData] = useState<OptionUpdatedJsonType>()
    const [colorVisible, setColorVisible] = useState(false)
    const [activeColorIndex, setActiveColorIndex] = useState<number | null>(null)
    const [tempColor, setTempColor] = useState<string>('#000000')
    const [colorValue, setColorValue] = useState<string>('')
    const [checkDuplicate, setCheckDuplicate] = useState('')
    const [duplicateLoading, setDuplicateLoading] = useState(false)
    const [duplicateError, setDuplicateError] = useState(false)
    const [visible, setIsVisible] = useState(false)
    const [dialogMessage, setDialogMessage] = useState<DialogType | null>(null)
    const [keyboardOpen, setKeyboardOpen] = useState(false)

    useEffect(() => {
        removeId = []
    }, [])

    useEffect(() => {
        if (optionId && edit) {
            setIsTransparentLoad(true)
            dispatch(getOnePosOptionList({ storeId: config.storeId, selectedId: optionId })).unwrap().then((res) => {
                if (res?.optionDetails) {
                    let addingField: OptionUpdatedValueType[] = []
                    res?.optionDetails?.optionvalues.map((item) => {
                        addingField.push({
                            id: item.id,
                            color: item.color || '#000000',
                            sortOrder: item.sortOrder,
                            optionValue: item.optionValue,
                            colorShown: res.optionDetails.showcolors,
                            isExist: false,
                            colorPicker: false,
                            colorOpen: false,
                            validColor: false
                        })
                    })
                    let optionDetails: OptionUpdatedJsonType = {
                        id: res?.optionDetails?.id,
                        description: res?.optionDetails?.description,
                        name: res?.optionDetails?.name,
                        optionValues: addingField,
                        colorShown: res?.optionDetails.showcolors
                    }
                    setOptionData(optionDetails)
                    setIsTransparentLoad(false)
                }
            })
                .catch((err) => {
                    setIsTransparentLoad(false)
                    console.log("err in getOnePosOptionList -- >", err)
                })
        }
    }, [optionId, isFocus])

    useEffect(() => {
        if (checkDuplicate != '') {
            setDuplicateLoading(true)
            const duplicateDebounceFunction = setTimeout(() => {
                let dup = checkDuplicate.trim()
                duplicateValidator(String(dup))
            }, 500)
            return () => clearTimeout(duplicateDebounceFunction)
        } else {
            setDuplicateLoading(false)
            setDuplicateError(false)
        }
    }, [checkDuplicate])

    useEffect(() => {
        if (visible && dialogMessage) {
            confirmationDialog(dialogMessage)
        }
    }, [dialogMessage])

    const validationSchema = Yup.object().shape({
        optionValueHeading: Yup.string().required('Option name is required'),
        colorShown: Yup.boolean(),
        optionValue: Yup.array().of(
            Yup.object().shape({
                optionValue: Yup.string().required('Option value is required'),
                color: Yup.string().when('colorShown', (colorShown) => {
                    if (colorShown[0]) {
                        return Yup.string().required('Color value is required')
                    } else {
                        return Yup.string().nullable()
                    }
                })
            })
        ),
        alternateName: Yup.string(),
    })

    const formik = useFormik({
        enableReinitialize: true,
        validationSchema: validationSchema,
        initialValues: {
            optionValueHeading: optionData?.name ? optionData?.name : '',
            optionValue: optionData?.optionValues ? optionData.optionValues : [{ id: 0, optionValue: '', color: '#000000', colorShown: false, colorPicker: false, isExist: false, sortOrder: null, colorOpen: false, validColor: true }],
            description: optionData?.description ? optionData?.description : '',
            colorShown: optionData ? optionData.colorShown : false
        },
        onSubmit: () => {
            Toast.show({
                type: 'info',
                text2: isExist ? "Duplicate values found" : 'Please fill all mandatory fields'
            })
        }
    })

    const duplicateValidator = (name: string) => {
        if (name != '') {
            let data = {
                value: formik?.values?.optionValueHeading,
                tableName: "options",
                columnName: "name",
                id: optionId ? optionId : null,
                storeId: true
            }
            dispatch(getOnePostDuplicateCheck({ storeId: config.storeId, data: data })).unwrap().then((response) => {
                setDuplicateLoading(false)
                if (response) {
                    setDuplicateError(response?.result?.alreadyExists)
                }
            })
                .catch((err: any) => {
                    setDuplicateLoading(false)
                    console.log("Error in getOnePostDuplicateCheck", err)
                })
        }
    }

    const colorHelper = (enabled: boolean) => {
        const updatedOptionValues = formik.values.optionValue.map((option: OptionUpdatedValueType) => {
            return {
                ...option,
                colorShown: enabled,
                validColor: enabled,
                color: option.color || '#000000'
            }
        })
        formik.setFieldValue('optionValue', updatedOptionValues)
        return updatedOptionValues
    }

    const isValidColor = (hexString: string) => {
        let validFormats = {
            hexRGB: /^#(?:[0-9a-fA-F]{3}){1,2}$/,
            hexRGBA: /^#(?:[0-9a-fA-F]{4}){1,2}$/,
            hexRRGGBB: /^#(?:[0-9a-fA-F]{2}){3}$/,
            hexRRGGBBAA: /^#(?:[0-9a-fA-F]{2}){4}$/
        }
        return validFormats.hexRGB.test(hexString) || validFormats.hexRGBA.test(hexString) || validFormats.hexRRGGBB.test(hexString) || validFormats.hexRRGGBBAA.test(hexString)
    }

    const overAllValidator = (duplicateDataExist?: OptionUpdatedValueType[]) => {
        let overallContent = [...formik.values.optionValue]
        let filterContent = duplicateDataExist ? duplicateDataExist : overallContent.filter((i) => i?.optionValue != '')
        let duplicates: number[] = []
        if (filterContent.length > 0) {
            for (let i = 0; i < filterContent.length; i++) {
                for (let j = i + 1; j < filterContent.length; j++) {
                    if (filterContent[j]?.optionValue != undefined) {
                        if (filterContent[i]?.optionValue.trim().toLowerCase() === filterContent[j]?.optionValue.trim().toLowerCase() && !duplicates?.includes(filterContent[i]?.id)) {
                            duplicates.push(filterContent[j]?.id)
                        }
                    }
                }
            }
        }
        return duplicates
    }

    const indexFormat = (data: OptionUpdatedValueType[]) => {
        if (data.length > 0) {
            for (let idx = 0; idx < data.length; idx++) {
                if (String(data[idx]?.id).length < 3) {
                    data[idx].id = idx
                }
            }
            return data
        }
        return data
    }

    const deleteHelper = (deleteId: number) => {
        setIsTransparentLoad(true)
        let deleteElement = [...formik.values.optionValue]
        let format
        let filter = deleteElement.filter((i) => i.id != deleteId)
        let duplicateData = [...overAllValidator()]
        if (!edit && filter) {
            format = indexFormat(filter)
            if (format) {
                if (duplicateData.length) {
                    let removedWithDuplicate = deleteOptionExist(format, overAllValidator(format))
                    formik.setFieldValue('optionValue', removedWithDuplicate)
                } else {
                    formik.setFieldValue('optionValue', format)
                }
            }
        } else {
            let storeDeleteId = deleteElement.filter((i) => i.id == deleteId)
            if (storeDeleteId.length > 0) {
                removeId.push(storeDeleteId[0].id)
            }
            format = indexFormat(filter)
            if (format) {
                if (duplicateData.length) {
                    let removedWithDuplicate = deleteOptionExist(format, overAllValidator(format))
                    formik.setFieldValue('optionValue', removedWithDuplicate)
                } else {
                    formik.setFieldValue('optionValue', format)
                }
            }
        }
        setIsTransparentLoad(false)
        Toast.show({
            type: 'success',
            text2: 'Option value deleted successfully'
        })
    }

    const deleteOptionExist = (existData: OptionUpdatedValueType[], existId: number[]) => {
        if (existData.length) {
            existData.forEach((data) => {
                data.isExist = existId.includes(data.id)
            })
            return existData
        }
    }

    const optionValueFormatter = (val: OptionUpdatedValueType[]) => {
        let optionValues: { id: number | null, optionValue: string, sortOrder: number | null, color: string }[] = []
        if (val.length) {
            val.forEach((i) => {
                optionValues.push({
                    id: edit ? String(i.id).length > 3 ? i.id : null : null,
                    optionValue: i.optionValue,
                    sortOrder: i.sortOrder,
                    color: formik.values.colorShown ? i.color : '#000000'
                })
            })
        }
        return optionValues
    }

    const addOptionData = () => {
        setIsTransparentLoad(true)
        let optionFormatter = optionValueFormatter([...formik.values.optionValue])
        let data = {
            "name": formik.values.optionValueHeading,
            "description": formik.values.description ? formik.values.description : null,
            "showColors": formik.values.colorShown,
            "optionValues": optionFormatter,
            "medium": "POS",
            "isPos": true,
            "removeId": removeId
        }
        if (edit) {
            dispatch(getOneUpdateOptionData({ storeId: config.storeId, updateId: optionId, data: data })).unwrap().then((res) => {
                if (res) {
                    setIsTransparentLoad(false)
                    navigation.goBack()
                    Toast.show({
                        type: 'success',
                        text2: 'Option values updated successfully'
                    })
                }
            })
                .catch((err: any) => {
                    setIsTransparentLoad(false)
                    console.log("Error in getOneUpdateOptionData", err)
                })
        } else {
            dispatch(getOnePostOptionData({ storeId: config.storeId, data: data })).unwrap().then((res) => {
                if (res) {
                    setIsTransparentLoad(false)
                    navigation.goBack()
                    Toast.show({
                        type: 'success',
                        text2: 'Option values added successfully'
                    })
                }
            })
                .catch((err: any) => {
                    setIsTransparentLoad(false)
                    console.log("Error in getOnePostOptionData", err)
                })
        }
    }

    const confirmationDialog = (msg: DialogType) => {
        if (msg?.id == 2) {
            navigation.goBack()
            setIsVisible(false)
        } else {
            setIsVisible(false)
            setDialogMessage(null)
        }
    }

    const comparingTwoArrays = (existValues: OptionUpdatedValueType[], updatedValues: OptionUpdatedValueType[]) => {
        if (existValues.length !== updatedValues.length) return 0
        for (let i = 0; i < existValues.length; i++) {
            if (existValues[i]?.optionValue !== updatedValues[i]?.optionValue || existValues[i]?.color !== updatedValues[i]?.color) {
                return 0
            }
        }
        return 1
    }

    const goBack = () => {
        if (formik.dirty) {
            setIsVisible(true)
        } else {
            navigation.goBack()
        }
    }

    useEffect(() => {
        const backHandler = BackHandler.addEventListener('hardwareBackPress', function () {
            goBack()
            return true
        })
        return () => backHandler.remove()
    }, [formik.dirty])

    const optionValuesErr = formik?.errors?.optionValue as FormikErrors<optionValue[]>
    const optionValueTouched = formik?.touched?.optionValue as FormikTouched<optionValue[]>
    let findElements = [...formik.values.optionValue]
    let isExist = findElements.filter((i) => i.isExist === true).length
    let isValid = findElements.filter((i) => i.validColor === true).length
    let isEmpty = findElements.filter((i) => i.optionValue == '').length

    useEffect(() => {
        const showSub = Keyboard.addListener("keyboardDidShow", () => setKeyboardOpen(true))
        const hideSub = Keyboard.addListener("keyboardDidHide", () => setKeyboardOpen(false))
        return () => {
            showSub.remove()
            hideSub.remove()
        }
    }, [])

    const addOptionValues = () => {
        let addOption = [...formik.values.optionValue]
        if ((!isEmpty && !isExist && !formik.values.colorShown) || (formik.values.colorShown && !isValid && !isEmpty && !isExist)) {
            addOption.push({
                id: formik.values.optionValue.length,
                optionValue: '',
                color: '#000000',
                colorShown: formik.values.colorShown,
                colorOpen: false,
                isExist: false,
                colorPicker: false,
                sortOrder: null,
                validColor: formik.values.colorShown
            })
            formik.setFieldValue('optionValue', addOption)
        } else {
            formik.handleSubmit()
        }
    }

    const sortOptionValues = () => {
        if (comparingTwoArrays(optionData?.optionValues ? optionData?.optionValues : [], formik.values.optionValue) && formik.values.colorShown === formik.initialValues.colorShown) {
            navigation.navigate('SortOptionList', { sortOptionValue: formik.values.optionValue, selectedId: props?.params?.selectedId, isList: false })
        } else {
            Toast.show({
                type: 'info',
                text2: 'You have unsaved changes. Please save first.'
            })
        }
    }

    const openColorPickerModal = (index: number, currentColor: string) => {
        setActiveColorIndex(index)
        setTempColor(currentColor || '#000000')
        setColorValue(currentColor || '#000000')
        setColorVisible(true)
    }

    const applySelectedColor = () => {
        if (activeColorIndex !== null) {
            let finalColor = colorValue && isValidColor(colorValue) ? colorValue : tempColor
            const updated = formik.values.optionValue.map((item, idx) => {
                if (idx === activeColorIndex) {
                    return { ...item, color: finalColor, validColor: false }
                }
                return item
            })
            formik.setFieldValue('optionValue', updated)
        }
        setColorVisible(false)
        setActiveColorIndex(null)
    }

    const isSaveDisabled = duplicateLoading || duplicateError

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <KeyboardAvoidingView
                    style={{ flex: 1, backgroundColor: '#F8FAFC' }}
                    behavior={Platform.OS === 'ios' ? "padding" : undefined}
                >
                    <View style={{ flex: 1, paddingBottom: Platform.OS === 'ios' && keyboardOpen ? 10 : undefined }}>
                        {/* Header with modern inline Cancel and Save/Update buttons */}
                        <CommonHeader
                            headerName={edit ? "Edit Option" : "Add Option"}
                            backIcon
                            backIconOnpress={goBack}
                            rightComponent={
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingRight: 4 }}>
                                    <TouchableOpacity
                                        onPress={goBack}
                                        activeOpacity={0.7}
                                        style={{
                                            backgroundColor: '#F1F5F9',
                                            borderWidth: 1,
                                            borderColor: '#CBD5E1',
                                            borderRadius: 8,
                                            paddingHorizontal: 16,
                                            paddingVertical: 8,
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <ZenText
                                            content="Cancel"
                                            bold
                                            size={fontSize.smallFontSize}
                                            color="#475569"
                                        />
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        disabled={isSaveDisabled}
                                        activeOpacity={0.8}
                                        onPress={() => {
                                            if (formik.dirty) {
                                                if ((!Object.keys(formik.errors).length && !isValid && !isExist && formik.values.colorShown) || (!formik.values.colorShown && !isExist && !Object.keys(formik.errors).length)) {
                                                    addOptionData()
                                                } else {
                                                    formik.handleSubmit()
                                                }
                                            } else {
                                                Toast.show({
                                                    type: 'info',
                                                    text2: 'You have no unsaved changes'
                                                })
                                            }
                                        }}
                                        style={{
                                            backgroundColor: isSaveDisabled ? '#94A3B8' : appColors.primary,
                                            borderRadius: 8,
                                            paddingHorizontal: 20,
                                            paddingVertical: 8,
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            opacity: isSaveDisabled ? 0.7 : 1
                                        }}
                                    >
                                        <ZenText
                                            content={edit ? "Update" : "Save"}
                                            bold
                                            size={fontSize.smallFontSize}
                                            color={appColors.light}
                                        />
                                    </TouchableOpacity>
                                </View>
                            }
                        />

                        <ScrollView
                            contentContainerStyle={{ padding: isTablet ? 20 : 12, paddingBottom: 40 }}
                            showsVerticalScrollIndicator={false}
                        >
                            {/* Card 1: Option Details */}
                            <View style={theme.addEditOptionStyle.card}>
                                <View style={theme.addEditOptionStyle.cardHeader}>
                                    <View style={theme.addEditOptionStyle.cardHeaderLeft}>
                                        <View style={theme.addEditOptionStyle.cardIconCircle}>
                                            <Icon name="tune-variant" type="material-community" size={20} color={appColors.primary} />
                                        </View>
                                        <View>
                                            <ZenText content="Option Details" bold size={16} color="#1E293B" />
                                            <ZenText content="Configure option name and presentation details" size={12} color="#64748B" />
                                        </View>
                                    </View>
                                </View>

                                {/* Form Fields: Responsive layout */}
                                <View style={{ flexDirection: isTablet ? 'row' : 'column', gap: 16, marginTop: 4 }}>
                                    {/* Option Name Input */}
                                    <View style={{ flex: 1 }}>
                                        <ZenInput
                                            value={formik.values.optionValueHeading}
                                            label="Option Name *"
                                            placeholder="e.g. Size, Color, Material"
                                            onBlur={formik.handleBlur('optionValueHeading')}
                                            onChangeText={(i: string) => {
                                                setCheckDuplicate(i.trim() !== '' ? i : '')
                                                formik.setFieldValue('optionValueHeading', i)
                                            }}
                                            placeholderTextColor="#94A3B8"
                                            inputStyle={theme.addEditOptionStyle.inpTxt}
                                            inputContainerStyle={[
                                                theme.addEditOptionStyle.inpCon,
                                                (duplicateError || (formik.touched.optionValueHeading && formik.errors.optionValueHeading)) && { borderColor: '#EF4444' }
                                            ]}
                                            rightIcon={
                                                duplicateLoading ? (
                                                    <ActivityIndicator size="small" color={appColors.primary} />
                                                ) : duplicateError ? (
                                                    <Icon name="alert-circle-outline" type="material-community" size={20} color="#EF4444" />
                                                ) : undefined
                                            }
                                            errorMessage={
                                                formik.touched.optionValueHeading && formik.errors.optionValueHeading
                                                    ? String(formik.errors.optionValueHeading)
                                                    : duplicateError
                                                        ? "This option name already exists"
                                                        : ''
                                            }
                                            errorStyle={theme.addEditOptionStyle.inpErr}
                                        />
                                    </View>

                                    {/* Description Input */}
                                    <View style={{ flex: 1 }}>
                                        <ZenInput
                                            value={formik.values.description}
                                            label="Description (Optional)"
                                            placeholder="Brief description for reference"
                                            onBlur={formik.handleBlur('description')}
                                            placeholderTextColor="#94A3B8"
                                            inputStyle={theme.addEditOptionStyle.inpTxt}
                                            inputContainerStyle={theme.addEditOptionStyle.inpCon}
                                            onChangeText={formik.handleChange('description')}
                                            errorMessage={formik.touched.description && formik.errors.description ? String(formik.errors.description) : undefined}
                                            errorStyle={theme.addEditOptionStyle.inpErr}
                                        />
                                    </View>
                                </View>

                                {/* Show Option Colors Checkbox Card */}
                                <View style={theme.addEditOptionStyle.colorCon}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <View style={{ flex: 1, paddingRight: 10 }}>
                                            <ZenText content="Display Color Swatches" bold size={14} color="#1E293B" />
                                            <ZenText
                                                content="Enable visual color pickers for each value (e.g. Red #FF0000, Blue #0000FF)"
                                                size={12}
                                                color="#64748B"
                                                style={{ marginTop: 2 }}
                                            />
                                        </View>
                                        <ZenCheckBox
                                            checked={formik.values.colorShown}
                                            checkedColor={appColors.primary}
                                            unCheckedColor="#94A3B8"
                                            onPress={() => {
                                                const nextValue = !formik.values.colorShown
                                                formik.setFieldValue('colorShown', nextValue)
                                                colorHelper(nextValue)
                                            }}
                                            checkedIcon="check-square"
                                            unCheckedIcon="square-o"
                                            containerStyle={{ padding: 0, margin: 0, backgroundColor: 'transparent', borderWidth: 0 }}
                                        />
                                    </View>
                                </View>
                            </View>

                            {/* Card 2: Option Values */}
                            <View style={theme.addEditOptionStyle.card}>
                                <View style={theme.addEditOptionStyle.cardHeader}>
                                    <View style={theme.addEditOptionStyle.cardHeaderLeft}>
                                        <View style={[theme.addEditOptionStyle.cardIconCircle, { backgroundColor: '#EEF2FF' }]}>
                                            <Icon name="palette-swatch-outline" type="material-community" size={20} color="#4F46E5" />
                                        </View>
                                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                            <ZenText content="Option Values" bold size={16} color="#1E293B" />
                                            <View style={theme.addEditOptionStyle.countBadge}>
                                                <ZenText
                                                    content={`${formik.values.optionValue.length} ${formik.values.optionValue.length === 1 ? 'item' : 'items'}`}
                                                    size={11}
                                                    bold
                                                    color="#475569"
                                                />
                                            </View>
                                        </View>
                                    </View>

                                    {/* Action Buttons: Sort and Add Value */}
                                    <View style={theme.addEditOptionStyle.actionButtonsRow}>
                                        {edit && formik.values.optionValue.length > 1 && (
                                            <TouchableOpacity
                                                style={{
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    backgroundColor: '#F1F5F9',
                                                    borderWidth: 1,
                                                    borderColor: '#CBD5E1',
                                                    borderRadius: 8,
                                                    paddingHorizontal: 12,
                                                    paddingVertical: 7
                                                }}
                                                onPress={sortOptionValues}
                                            >
                                                <Icon name="sort" type="material" size={16} color="#475569" />
                                                <ZenText content="Sort" bold size={12} color="#475569" />
                                            </TouchableOpacity>
                                        )}

                                        <TouchableOpacity
                                            style={{
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                gap: 6,
                                                backgroundColor: appColors.primary,
                                                borderRadius: 8,
                                                paddingHorizontal: 14,
                                                paddingVertical: 7
                                            }}
                                            onPress={addOptionValues}
                                        >
                                            <Icon name="plus" type="feather" size={16} color={appColors.light} />
                                            <ZenText content="Add Value" bold size={12} color={appColors.light} />
                                        </TouchableOpacity>
                                    </View>
                                </View>

                                {/* Option Values List: 2-column grid on tablet / wide screen */}
                                <View
                                    style={{
                                        flexDirection: isTablet ? 'row' : 'column',
                                        flexWrap: 'wrap',
                                        justifyContent: 'space-between',
                                        rowGap: 8,
                                        marginTop: 6
                                    }}
                                >
                                    {formik.values.optionValue.map((fieldValue: OptionUpdatedValueType, fieldIndex: number) => {
                                        const errorMsg =
                                            formik?.touched?.optionValue && optionValueTouched && optionValueTouched[fieldIndex]?.optionValue && optionValuesErr?.length && optionValuesErr[fieldIndex]?.optionValue
                                                ? String(optionValuesErr[fieldIndex]?.optionValue)
                                                : formik?.values?.colorShown && formik.values.optionValue[fieldIndex].validColor
                                                    ? "Color value is required"
                                                    : formik?.values?.optionValue[fieldIndex].isExist
                                                        ? "This value already exists"
                                                        : ""
                                        const hasError = Boolean(errorMsg)

                                        return (
                                            <View
                                                key={fieldIndex}
                                                style={[
                                                    theme.addEditOptionStyle.valueItemCard,
                                                    {
                                                        width: isTablet ? '49.2%' : '100%',
                                                        paddingVertical: 8,
                                                        paddingHorizontal: 10
                                                    },
                                                    hasError && { borderColor: '#FCA5A5', backgroundColor: '#FEF2F2' }
                                                ]}
                                            >
                                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                                    {/* Index Badge */}
                                                    <View style={theme.addEditOptionStyle.indexBadge}>
                                                        <ZenText content={`#${fieldIndex + 1}`} size={11} bold color="#475569" />
                                                    </View>

                                                    {/* Option Value Input */}
                                                    <View style={{ flex: 1, justifyContent: 'center' }}>
                                                        <ZenInput
                                                            renderErrorMessage={false}
                                                            placeholder={`Option Value ${fieldIndex + 1} (e.g. Small, 5kg)`}
                                                            value={formik.values.optionValue[fieldIndex].optionValue}
                                                            containerStyle={{ paddingHorizontal: 0, marginVertical: 0 }}
                                                            inputContainerStyle={[
                                                                theme.addEditOptionStyle.inpCon,
                                                                { height: 38, backgroundColor: '#FFFFFF', paddingHorizontal: 8 },
                                                                hasError && { borderColor: '#EF4444' }
                                                            ]}
                                                            inputStyle={[theme.addEditOptionStyle.inpTxt, { fontSize: 13, minHeight: 38, height: 38, paddingVertical: 0 }]}
                                                            placeholderTextColor="#94A3B8"
                                                            onBlur={() => {
                                                                formik.setFieldTouched(`optionValue[${fieldIndex}].optionValue`, true)
                                                            }}
                                                            onChangeText={(changeText: string) => {
                                                                formik.setFieldValue(`optionValue[${fieldIndex}].optionValue`, changeText)
                                                            }}
                                                            onEndEditing={() => {
                                                                let duplicate = overAllValidator()
                                                                let validator = [...formik.values.optionValue]
                                                                if (validator.length) {
                                                                    validator.forEach((i) => {
                                                                        i.isExist = duplicate.includes(i.id)
                                                                    })
                                                                    formik.setFieldValue('optionValue', validator)
                                                                }
                                                            }}
                                                        />
                                                    </View>

                                                    {/* Color Swatch Trigger (if colorShown enabled) */}
                                                    {formik.values.colorShown && (
                                                        <TouchableOpacity
                                                            style={theme.addEditOptionStyle.colorSwatchBtn}
                                                            onPress={() => {
                                                                openColorPickerModal(fieldIndex, fieldValue.color)
                                                            }}
                                                        >
                                                            <View
                                                                style={[
                                                                    theme.addEditOptionStyle.colorSwatchPreview,
                                                                    { backgroundColor: fieldValue.color || '#000000' }
                                                                ]}
                                                            />
                                                            <ZenText
                                                                content={fieldValue.color || '#000000'}
                                                                size={11}
                                                                bold
                                                                color="#334155"
                                                            />
                                                            <Icon name="eyedropper" type="material-community" size={14} color="#64748B" />
                                                        </TouchableOpacity>
                                                    )}

                                                    {/* Delete Button */}
                                                    <TouchableOpacity
                                                        style={theme.addEditOptionStyle.deleteBtn}
                                                        onPress={() => {
                                                            if (formik.values.optionValue.length > 1) {
                                                                deleteHelper(fieldValue.id)
                                                            } else {
                                                                Toast.show({
                                                                    type: 'info',
                                                                    text2: 'At least one option value is required'
                                                                })
                                                            }
                                                        }}
                                                    >
                                                        <Icon name="trash-can-outline" type="material-community" size={18} color="#DC2626" />
                                                    </TouchableOpacity>
                                                </View>

                                                {/* Error message positioned cleanly below the aligned row */}
                                                {hasError && (
                                                    <ZenText
                                                        content={errorMsg}
                                                        size={11}
                                                        bold
                                                        color="#EF4444"
                                                        style={{ marginTop: 4, marginLeft: 34 }}
                                                    />
                                                )}
                                            </View>
                                        )
                                    })}
                                </View>
                            </View>

                            {/* Loading Modal */}
                            <CustomModal visible={isTransparentLoad} transparent={true}>
                                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: appColors.transparent }}>
                                    <ZenLoader lottie={require("../../../assets/infinityLoader.json")} lottieType="Local" />
                                </View>
                            </CustomModal>

                            {/* Unsaved Changes Leave Confirmation */}
                            <Popover isVisible={visible}>
                                <CommonDialogBox type="EDIT" replaceValue="option" confirmation={confirmationDialog} />
                            </Popover>
                        </ScrollView>

                        {/* Modern Color Picker Dialog Modal */}
                        <CustomModal
                            visible={colorVisible && activeColorIndex !== null}
                            transparent={true}
                            animationType="fade"
                            onRequestClose={() => {
                                setColorVisible(false)
                                setActiveColorIndex(null)
                            }}
                        >
                            <View style={{ flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.55)', justifyContent: 'center', alignItems: 'center', padding: 16 }}>
                                <View
                                    style={{
                                        backgroundColor: '#FFFFFF',
                                        borderRadius: 16,
                                        width: '100%',
                                        maxWidth: 440,
                                        padding: 20,
                                        shadowColor: '#000',
                                        shadowOffset: { width: 0, height: 4 },
                                        shadowOpacity: 0.15,
                                        shadowRadius: 10,
                                        elevation: 6
                                    }}
                                >
                                    {/* Modal Header */}
                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 14, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' }}>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                                            <View style={{ width: 26, height: 26, borderRadius: 13, backgroundColor: tempColor, borderWidth: 1.5, borderColor: '#CBD5E1' }} />
                                            <View>
                                                <ZenText content="Select Color Value" bold size={16} color="#1E293B" />
                                                <ZenText content={tempColor.toUpperCase()} size={12} color="#64748B" />
                                            </View>
                                        </View>
                                        <TouchableOpacity
                                            onPress={() => {
                                                setColorVisible(false)
                                                setActiveColorIndex(null)
                                            }}
                                            style={{ padding: 4 }}
                                        >
                                            <Icon name="close" type="material-community" size={22} color="#64748B" />
                                        </TouchableOpacity>
                                    </View>

                                    {/* Color Presets Palette */}
                                    <View style={{ marginTop: 14 }}>
                                        <ZenText content="Quick Presets" size={12} bold color="#64748B" style={{ marginBottom: 8 }} />
                                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                                            {COLOR_PRESETS.map((preset) => (
                                                <TouchableOpacity
                                                    key={preset}
                                                    onPress={() => {
                                                        setTempColor(preset)
                                                        setColorValue(preset)
                                                    }}
                                                    style={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: 16,
                                                        backgroundColor: preset,
                                                        borderWidth: tempColor.toLowerCase() === preset.toLowerCase() ? 2.5 : 1,
                                                        borderColor: tempColor.toLowerCase() === preset.toLowerCase() ? appColors.primary : '#CBD5E1',
                                                        justifyContent: 'center',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    {tempColor.toLowerCase() === preset.toLowerCase() && (
                                                        <Icon
                                                            name="check"
                                                            type="material-community"
                                                            size={16}
                                                            color={preset === '#FFFFFF' || preset === '#F59E0B' ? '#000' : '#FFF'}
                                                        />
                                                    )}
                                                </TouchableOpacity>
                                            ))}
                                        </View>
                                    </View>

                                    {/* Wheel Color Picker */}
                                    <View style={{ paddingVertical: 14, alignItems: 'center' }}>
                                        <View style={{ width: '100%', height: 180 }}>
                                            <ColorPicker
                                                color={tempColor}
                                                gapSize={12}
                                                onColorChangeComplete={(color) => {
                                                    setTempColor(color)
                                                    setColorValue(color)
                                                }}
                                                autoResetSlider={true}
                                                discreteLength={10}
                                                thumbSize={24}
                                                shadeWheelThumb={true}
                                                sliderSize={24}
                                                noSnap={true}
                                                row={false}
                                                swatchesLast={false}
                                                swatches={false}
                                                useNativeDriver={false}
                                                useNativeLayout={false}
                                            />
                                        </View>
                                    </View>

                                    {/* Hex Code Input */}
                                    <View style={{ marginTop: 50 }}>
                                        <ZenInput
                                            label="Hex Color Code"
                                            maxLength={7}
                                            value={colorValue || tempColor}
                                            placeholder="#000000"
                                            onChangeText={(i) => {
                                                setColorValue(i)
                                                if (isValidColor(i)) {
                                                    setTempColor(i)
                                                }
                                            }}
                                            inputContainerStyle={[theme.addEditOptionStyle.inpCon, { height: 40 }]}
                                            inputStyle={theme.addEditOptionStyle.inpTxt}
                                        />
                                    </View>

                                    {/* Modal Action Buttons */}
                                    <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                                        <TouchableOpacity
                                            onPress={() => {
                                                setColorVisible(false)
                                                setActiveColorIndex(null)
                                            }}
                                            activeOpacity={0.7}
                                            style={{
                                                backgroundColor: '#F1F5F9',
                                                borderWidth: 1,
                                                borderColor: '#CBD5E1',
                                                borderRadius: 8,
                                                paddingHorizontal: 16,
                                                paddingVertical: 8,
                                                justifyContent: 'center',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <ZenText
                                                content="Cancel"
                                                bold
                                                size={fontSize.smallFontSize}
                                                color="#475569"
                                            />
                                        </TouchableOpacity>
                                        <TouchableOpacity
                                            disabled={isSaveDisabled}
                                            activeOpacity={0.8}
                                            onPress={applySelectedColor}
                                            style={{
                                                backgroundColor: isSaveDisabled ? '#94A3B8' : appColors.primary,
                                                borderRadius: 8,
                                                paddingHorizontal: 20,
                                                paddingVertical: 8,
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                opacity: isSaveDisabled ? 0.7 : 1
                                            }}
                                        >
                                            <ZenText
                                                content={"Apply Color"}
                                                bold
                                                size={fontSize.smallFontSize}
                                                color={appColors.light}
                                            />
                                        </TouchableOpacity>


                                    </View>
                                </View>
                            </View>
                        </CustomModal>
                    </View>
                </KeyboardAvoidingView>
            )}
        </ThemeConsumer>
    )
}

export default AddEditOption